import { useState } from "react";
import { Button } from "@/components/primitives/button";
import { formatFileSize } from "@/lib/utils";
import { saveFile } from "@/lib/saveFile";
import { MessageEntity } from "@/indexedDB/types";
import { HardDriveDownload, File, Loader } from "lucide-react";
import { TruncatedFilename } from "@/components/primitives/TruncatedFilename";
import { processFileAttachment } from "@/middleware/attachments";

export default function FileAttachment({
  message,
}: {
  message: MessageEntity;
}) {
  const [downloading, setDownloading] = useState(false);

  const fileMeta = message.content.file;

  async function handleDownload() {
    if (!fileMeta || downloading) {
      return;
    }

    setDownloading(true);
    try {
      const blob = await processFileAttachment({
        chatId: message.chatId,
        messageId: message.id,
        timestamp: message.timestamp,
        attachmentMeta: fileMeta,
      });

      if (!blob) {
        return;
      }

      await saveFile(blob, fileMeta.filename);
    } finally {
      setDownloading(false);
    }
  }

  if (!fileMeta?.filename) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex size-10 shrink-0 items-center justify-center rounded-sm">
        <File strokeWidth={1} className="size-full" />
      </div>

      <div className="text-md flex w-full min-w-0 flex-1 flex-col overflow-hidden">
        <TruncatedFilename
          filename={fileMeta.filename}
          className="text-sm font-medium"
        />
        <span className="text-xs text-muted-2">
          {formatFileSize(fileMeta.fileSize || 0)}
        </span>
      </div>

      <Button
        type="button"
        aria-label="Download file"
        onClick={handleDownload}
        disabled={downloading}
        size="icon"
        variant="ghost"
        className="ml-2 shrink-0 text-secondary opacity-70"
      >
        {downloading ? (
          <Loader size={16} className="animate-spin" />
        ) : (
          <HardDriveDownload size={16} />
        )}
      </Button>
    </div>
  );
}
